/**
 * Results Page
 * 
 * Unified page with tabs for Time Trials and Race Results
 * - Tab navigation (Time Trials | Race Results)
 * - URL query parameter for tab selection
 * - Renders appropriate component based on selected tab
 * 
 * Requirements: 14.1-14.7
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TimeTrials } from './TimeTrials';
import { RaceResults } from '../components/features/raceResults/RaceResults';

type TabType = 'time-trials' | 'race-results';

export function Results() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('time-trials');

  // Read tab from URL query parameter on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'race-results') {
      setActiveTab('race-results');
    } else {
      // Default to time-trials
      setActiveTab('time-trials');
    }
  }, [searchParams]);

  /**
   * Handle tab change
   */
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  return (
    <div className="container mx-auto px-2 md:px-4 py-8">
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('time-trials')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'time-trials'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              aria-current={activeTab === 'time-trials' ? 'page' : undefined}
            >
              Time Trials
            </button>
            <button
              onClick={() => handleTabChange('race-results')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'race-results'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              aria-current={activeTab === 'race-results' ? 'page' : undefined}
            >
              Race Results
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'time-trials' && <TimeTrials />}
        {activeTab === 'race-results' && <RaceResults />}
      </div>
    </div>
  );
}

export default Results;

